import { useCallback, useMemo } from 'react';
import useAuth from 'src/hooks/useAuth';
import { useSubscriptionPermissionsContext } from 'src/mindtime/contexts/SubscriptionPermissionsContext';
import {
  componentPermission,
  modulePermission,
} from 'src/mindtime/config/subscriptionPermissionRegistry';
import {
  buildSubscriptionAccessContext,
  canAccess,
  resolveClientSubscribedAccess,
  resolveEdrPermissionsReady,
} from 'src/mindtime/utils/subscriptionPermissions';
import useHasPermissions from 'src/hooks/useHasPermission';
import { useIsResellerOrg, useOrganizationTypeName } from 'src/mindtime/hooks/useIsResellerOrg';

export const useSubscriptionPermissionAccess = () => {
  const checkRole = useHasPermissions();
  const { isInitialized, user } = useAuth();
  const isReseller = useIsResellerOrg();
  const organizationTypeName = useOrganizationTypeName();
  const { resolved, orgEntitlements, subscriptionKeys, roleKeys, hasActiveSubscription } =
    useSubscriptionPermissionsContext();

  const clientSubscribed = resolveClientSubscribedAccess(
    isReseller,
    orgEntitlements,
    hasActiveSubscription,
  );

  const permissionsReady = resolveEdrPermissionsReady({
    isReseller,
    authInitialized: isInitialized,
    hasAuthUser: Boolean(user),
    subscriptionResolved: resolved,
  });

  const accessContext = useMemo(
    () =>
      buildSubscriptionAccessContext({
        isReseller,
        organizationTypeName,
        orgEntitlements,
        hasActiveSubscription,
        roleKeys,
        subscriptionKeys,
        checkRole,
      }),
    [isReseller, organizationTypeName, orgEntitlements, hasActiveSubscription, roleKeys, subscriptionKeys, checkRole],
  );

  const hasModuleAccess = useCallback(
    (modulePermissionKey: string) => {
      if (!permissionsReady) return false;
      return canAccess(accessContext, modulePermissionKey, { blockPreviewCreate: true });
    },
    [permissionsReady, accessContext],
  );

  const hasAccess = useCallback(
    (componentPermissionKey: string, parentPermissionKey: string) => {
      if (!permissionsReady) return false;
      return canAccess(accessContext, componentPermissionKey, {
        parentPermission: parentPermissionKey,
      });
    },
    [permissionsReady, accessContext],
  );

  return {
    hasAccess,
    hasModuleAccess,
    permissionsResolved: permissionsReady,
    hasActiveSubscription: clientSubscribed,
    isReseller,
    accessContext,
  };
};

/**
 * Module-scoped subscription access for any solution.
 * Register solution + module once in `subscriptionPermissionRegistry.ts`;
 * pages only reference component keys (e.g. `can('summary')`).
 */
export const useSubscriptionModulePermissions = (productId: string, moduleId: string) => {
  const { hasAccess, hasModuleAccess, permissionsResolved, hasActiveSubscription, isReseller } =
    useSubscriptionPermissionAccess();
  const parent = modulePermission(productId, moduleId);

  const canModule = hasModuleAccess(parent);

  const canWidget = useCallback(
    (componentKey: string) => {
      if (!permissionsResolved) return false;
      return hasAccess(componentPermission(productId, moduleId, componentKey), parent);
    },
    [permissionsResolved, hasAccess, productId, moduleId, parent],
  );

  const can = canWidget;

  return {
    canModule,
    can,
    canWidget,
    parentPermission: parent,
    permissionsResolved,
    hasActiveSubscription,
    isReseller,
  };
};
